import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { fmtEuro } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Database, RefreshCw, Lock, Unlock } from "lucide-react";
import { ImportStatusCards } from "@/components/ImportStatusCards";

const M = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μάι", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];
const SEC = [["revenue", "Έσοδα"], ["opex", "Λειτουργικά έξοδα"], ["payroll", "Μισθοδοσία (συνολικό κόστος)"], ["below", "Κάτω από EBITDA"]];

export default function Ledger() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());
  const [entity, setEntity] = useState("GROUP");
  const { data } = useQuery({ queryKey: ["facts-summary", year, entity], queryFn: async () => (await api.get(`/facts/summary?year=${year}&entity=${entity}`)).data });
  const { data: facts } = useQuery({ queryKey: ["facts-locks", year], queryFn: async () => (await api.get(`/facts?year=${year}`)).data });
  const locked = new Set((facts || []).filter((f) => f.locked).map((f) => f.month));
  const rebuild = useMutation({ mutationFn: async () => (await api.post(`/facts/rebuild?year=${year}`)).data,
    onSuccess: (d) => { toast.success(`Ανανεώθηκε: ${d.rows} γραμμές`); qc.invalidateQueries({ queryKey: ["facts-summary"] }); qc.invalidateQueries({ queryKey: ["facts-status"] }); },
    onError: (e) => toast.error(apiErr(e)) });
  const lock = useMutation({ mutationFn: async ({ month, l }) => (await api.put("/facts/lock", { year, month, locked: l })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["facts-locks"] }); toast.success("Ενημερώθηκε"); }, onError: (e) => toast.error(apiErr(e)) });
  return (
    <div className="space-y-6" data-wide data-testid="ledger-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">Αποτελέσματα</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><Database className="h-7 w-7" /> Ενιαίο Καθολικό</h1>
          <p className="text-sm text-[#6F6A60] mt-1">Μία πηγή αριθμών για Dashboard, P&L, Budget και KPIs. {data?.rebuilt_at && <>Τελευταία ανανέωση {data.rebuilt_at.slice(0, 16).replace("T", " ")}</>}</p>
        </div>
        <select data-testid="ledger-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm">
          {[year - 2, year - 1, year, year + 1].filter((y, i, a) => a.indexOf(y) === i).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select data-testid="ledger-entity" value={entity} onChange={(e) => setEntity(e.target.value)} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm">
          <option value="GROUP">Όμιλος (άθροισμα εταιρειών)</option>
          {(data?.entities || []).map((e) => <option key={e.code} value={e.code}>{e.name}</option>)}
        </select>
        {["owner", "hr", "manager"].includes(user?.role) && (
          <button data-testid="ledger-rebuild" onClick={() => rebuild.mutate()} disabled={rebuild.isPending} className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${rebuild.isPending ? "animate-spin" : ""}`} /> Ανανέωση από πηγές
          </button>
        )}
      </div>
      <ImportStatusCards year={year} />
      {data && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#45423C] text-white/80 text-[10px] uppercase tracking-wider">
              <th className="px-3 py-2 text-left">Ενότητα</th>{M.map((m, i) => <th key={m} className="px-2 py-2 text-right">{m}{locked.has(i + 1) && <Lock className="inline h-3 w-3 ml-1" />}</th>)}<th className="px-3 py-2 text-right">Σύνολο</th>
            </tr></thead>
            <tbody>
              {SEC.map(([k, l]) => (
                <tr key={k} className="border-t border-[#E3DED4]" data-testid={`ledger-row-${k}`}>
                  <td className="px-3 py-2 font-medium text-[#45423C]">{l}</td>
                  {M.map((_, i) => <td key={i} className="px-2 py-2 text-right tabular">{fmtEuro(data.sections[k][String(i + 1)])}</td>)}
                  <td className="px-3 py-2 text-right tabular font-semibold">{fmtEuro(data.totals[k])}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-[#45423C] bg-[#F7F5F1] font-semibold" data-testid="ledger-row-result">
                <td className="px-3 py-2">Αποτέλεσμα</td>
                {M.map((_, i) => <td key={i} className={`px-2 py-2 text-right tabular ${data.result_months[String(i + 1)] < 0 ? "text-[#A1493A]" : "text-[#3F6B4C]"}`}>{fmtEuro(data.result_months[String(i + 1)])}</td>)}
                <td className="px-3 py-2 text-right tabular">{fmtEuro(data.result)}</td>
              </tr>
              {user?.role === "owner" && (
                <tr className="border-t border-[#E3DED4]"><td className="px-3 py-2 text-xs text-[#8A8577]">Κλείδωμα μήνα</td>
                  {M.map((_, i) => <td key={i} className="px-2 py-1 text-right">
                    <button data-testid={`ledger-lock-${i + 1}`} onClick={() => lock.mutate({ month: i + 1, l: !locked.has(i + 1) })} className="text-[#8A8577] hover:text-[#45423C]">{locked.has(i + 1) ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}</button>
                  </td>)}<td /></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="rounded-2xl bg-white border border-[#E3DED4] p-4" data-testid="ledger-by-source">
            <h3 className="font-medium text-[#45423C] mb-2">Ανά πηγή</h3>
            {Object.entries(data.by_source).map(([k, v]) => <div key={k} className="flex justify-between py-1 border-t border-[#F1EDE6]"><span className="text-[#6F6A60]">{k}</span><span className="tabular">{fmtEuro(v)}</span></div>)}
          </div>
          <div className="rounded-2xl bg-white border border-[#E3DED4] p-4" data-testid="ledger-by-entity">
            <h3 className="font-medium text-[#45423C] mb-2">Ανά εταιρεία</h3>
            {Object.entries(data.by_entity).map(([k, v]) => (
              <div key={k} className="py-1.5 border-t border-[#F1EDE6]" data-testid={`ledger-entity-${k}`}>
                <div className="flex justify-between font-medium text-[#45423C]"><span>{(data.entities.find((e) => e.code === k) || {}).name || k}</span><span className={`tabular ${v.result < 0 ? "text-[#A1493A]" : "text-[#3F6B4C]"}`}>{fmtEuro(v.result)}</span></div>
                <div className="flex justify-between text-xs text-[#8A8577]"><span>Έσοδα {fmtEuro(v.revenue)}</span><span>Κόστος {fmtEuro(v.costs)}</span></div>
              </div>
            ))}
            <div className="flex justify-between py-1 border-t border-[#F1EDE6] text-xs text-[#8A8577]"><span>Ενδοομιλικά προς έλεγχο (μονόπλευρα)</span><span className="tabular">{fmtEuro(data.intercompany_flagged - data.intercompany_eliminated)}</span></div>
            <div className="flex justify-between py-1 text-xs text-[#8A8577]"><span>Ενδοομιλικά που απαλείφθηκαν (και οι δύο πλευρές)</span><span className="tabular">{fmtEuro(data.intercompany_eliminated)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
